/**
 * ClientTypeBadge Component for Web Portal
 * 
 * Displays a visual indicator of client type in chat interfaces.
 * 
 * Badge logic per requirements 20.1, 20.2, 20.3, 20.4:
 * - If caseStatus === 'INQUIRY': Show "INQUIRY" badge (yellow/orange)
 * - If isEnabled === false: Show "DISABLED" badge (gray/red)
 * - Otherwise: Show "CLIENT" badge (blue) or display caseNumber if provided
 */

import { Badge } from '../ui/badge';

/**
 * Case status values matching the backend CaseStatus enum
 * Defined locally to avoid Vite build issues with symlinked packages
 */
export const CASE_STATUS = {
  INQUIRY: 'INQUIRY',
  NEW: 'NEW',
  IN_PROGRESS: 'IN_PROGRESS',
  NEGOTIATION: 'NEGOTIATION',
  LITIGATION: 'LITIGATION',
  SETTLED: 'SETTLED',
  CLOSED: 'CLOSED',
} as const;

export type CaseStatusType = typeof CASE_STATUS[keyof typeof CASE_STATUS];

export interface ClientTypeBadgeProps {
  /** The case status of the client's case */
  caseStatus?: CaseStatusType | string;
  /** Whether the user account is enabled */
  isEnabled: boolean;
  /** Optional case number to display instead of "CLIENT" */
  caseNumber?: string;
  /** Size variant for the badge */
  size?: 'small' | 'medium' | 'large';
  /** User role - used to show PROSPECT badge when no case exists */
  userRole?: string;
}

type BadgeConfig = {
  label: string;
  variant: 'warning' | 'destructive' | 'info';
  className?: string;
};

/**
 * Determines the badge configuration based on case status and account enabled state.
 * Priority order:
 * 1. INQUIRY status (yellow/orange)
 * 2. Disabled account (gray/red)
 * 3. PROSPECT user without case (yellow/orange)
 * 4. CLIENT badge (blue) or case number
 */
function getBadgeConfig(
  caseStatus?: CaseStatusType | string,
  isEnabled?: boolean,
  caseNumber?: string,
  userRole?: string
): BadgeConfig {
  // Priority 1: INQUIRY status (yellow/orange)
  if (caseStatus === CASE_STATUS.INQUIRY) {
    return { label: 'INQUIRY', variant: 'warning' };
  }

  // Priority 2: Disabled account (gray/red)
  if (!isEnabled) {
    return { 
      label: 'DISABLED', 
      variant: 'destructive',
      className: 'bg-gray-100 text-gray-700 border-gray-300'
    };
  }

  // Priority 3: PROSPECT user without case (yellow/orange)
  if (userRole === 'PROSPECT' && !caseStatus) {
    return { label: 'PROSPECT', variant: 'warning' };
  }

  // Priority 4: CLIENT badge (blue) or case number
  return {
    label: caseNumber || 'CLIENT',
    variant: 'info',
  };
}

export function ClientTypeBadge({
  caseStatus,
  isEnabled,
  caseNumber,
  size = 'medium',
  userRole,
}: ClientTypeBadgeProps) {
  const config = getBadgeConfig(caseStatus, isEnabled, caseNumber, userRole);

  const sizeClasses = {
    small: 'text-xs px-1.5 py-0.5',
    medium: 'text-xs px-2.5 py-0.5',
    large: 'text-sm px-3 py-1',
  };

  return (
    <Badge
      variant={config.variant}
      className={`${sizeClasses[size]} ${config.className || ''}`}
    >
      {config.label}
    </Badge>
  );
}

export default ClientTypeBadge;
